import { useEffect, useRef, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

/**
 * Uses the native <dialog> element so focus trapping, Esc-to-close and the
 * backdrop come from the platform rather than hand-rolled key handlers.
 */
export function Modal({ open, title, onClose, children, footer, wide }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Clicking the backdrop (the dialog element itself) dismisses.
        if (event.target === ref.current) onClose();
      }}
      className={`w-[92vw] border-2 border-brass-400 bg-field-900 p-0 text-field-100
        backdrop:bg-black/80 ${wide ? 'max-w-3xl' : 'max-w-lg'}`}
    >
      <form method="dialog" onSubmit={(event) => event.preventDefault()}>
        <div className="flex items-center justify-between border-b border-field-800 bg-field-950 px-5 py-3">
          <h2 className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-field-50">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost px-2 py-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-field-800 px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </form>
    </dialog>
  );
}
